import { useMemo } from 'react';

import type {
  RankOption,
  RegimeOption,
} from '../components/special-award-regimes-source';

import {
  AWARDING_LOGIC_DICTIONARY_TYPE,
  ELIGIBILITY_CRITERIA_DICTIONARY_TYPE,
  ELIGIBILITY_CRITERIA_OPTIONS,
  ELIGIBILITY_OPERAND_DICTIONARY_TYPE,
  ELIGIBILITY_OPERAND_LOCAL_LABELS,
  ELIGIBILITY_OPERAND_OPTIONS,
} from '../calculations-step.constants';
import { buildDictionaryOptions } from '../helpers/build-dictionary-options';
import type { DictionaryDrivenOption } from '../helpers/build-dictionary-options';
import type {
  SpecialAwardLogicCategory,
  SpecialAwardRegime,
} from './use-special-award-form';

import { getRangesLabel } from '../../matrix-step/definition-tab.constants';

import {
  useAcademicRanks,
  useCustomLogic,
  useRegimes,
} from '../../../hooks/use-aid-regimes';

import { useDictionaryByType } from 'hooks/use-dictionary-by-type';

import type {
  AcademicRankDto,
  CalculationRuleRequest,
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';

const DEFAULT_RANGE_LABEL = 'Need range(s)';

export interface UseSpecialAwardModalDataParams {
  userId: string;
  modelId: string | number;
  clientId: string | number;
}

export interface UseSpecialAwardModalDataResult {
  regimes: SpecialAwardRegime[];
  regimeOptions: RegimeOption[];
  /** Label for the ranges select, derived from the first regime's metric. */
  rangeLabel: string;
  rankOptions: RankOption[];
  isRanksLoading: boolean;
  customLogicCategories: SpecialAwardLogicCategory[];
  isCustomLogicLoading: boolean;
  /**
   * The regimes / custom-logic queries back the edit prefill, which resolves
   * persisted ids against them — it must not run against empty lists.
   */
  isPrefillReady: boolean;
  /**
   * First `AWARDING_TYPE` dictionary entry, applied to new rules because the
   * backend requires `awardLogic` and the modal has no input for it (ADO #36245).
   * `undefined` until the dictionary resolves, which gates submit.
   */
  defaultAwardLogic: CalculationRuleRequest['awardLogic'];
  eligibilityCriteriaOptions: DictionaryDrivenOption<EligibilityCriteriaDtoCriteria>[];
  eligibilityOperandOptions: DictionaryDrivenOption<EligibilityCriteriaDtoOperand>[];
}

/**
 * Every server-owned list the "Add special award" modal renders from: the
 * regimes and their academic ranks, the client's custom-logic dimensions, and
 * the three dictionaries behind the awarding-logic default and the two
 * eligibility dropdowns. Keeps the query orchestration out of the modal, which
 * is then purely the form's shell.
 */
export const useSpecialAwardModalData = ({
  userId,
  modelId,
  clientId,
}: UseSpecialAwardModalDataParams): UseSpecialAwardModalDataResult => {
  const regimesQuery = useRegimes({
    userId,
    modelId,
    enabled: !!userId && !!modelId,
  });
  const regimes = useMemo(
    () => (regimesQuery.data ?? []) as SpecialAwardRegime[],
    [regimesQuery.data]
  );

  const customLogicQuery = useCustomLogic({
    userId,
    clientId,
    enabled: !!userId && !!clientId,
  });
  const customLogicCategories = useMemo(
    () => (customLogicQuery.data ?? []) as SpecialAwardLogicCategory[],
    [customLogicQuery.data]
  );

  const academicRanksQuery = useAcademicRanks({
    userId,
    aidModelId: modelId,
    enabled: !!userId && !!modelId,
  });
  const academicRanks = (academicRanksQuery.data ?? []) as AcademicRankDto[];

  const awardLogicDictionaryQuery = useDictionaryByType(
    userId,
    AWARDING_LOGIC_DICTIONARY_TYPE,
    { enabled: !!userId }
  );

  const eligibilityCriteriaDictionaryQuery = useDictionaryByType(
    userId,
    ELIGIBILITY_CRITERIA_DICTIONARY_TYPE,
    { enabled: !!userId }
  );
  const eligibilityCriteriaOptions = useMemo(
    () =>
      buildDictionaryOptions(
        ELIGIBILITY_CRITERIA_OPTIONS,
        eligibilityCriteriaDictionaryQuery.data
      ),
    [eligibilityCriteriaDictionaryQuery.data]
  );

  const eligibilityOperandDictionaryQuery = useDictionaryByType(
    userId,
    ELIGIBILITY_OPERAND_DICTIONARY_TYPE,
    { enabled: !!userId }
  );
  const eligibilityOperandOptions = useMemo(
    () =>
      buildDictionaryOptions(
        ELIGIBILITY_OPERAND_OPTIONS,
        eligibilityOperandDictionaryQuery.data,
        ELIGIBILITY_OPERAND_LOCAL_LABELS
      ),
    [eligibilityOperandDictionaryQuery.data]
  );

  const regimeOptions = useMemo<RegimeOption[]>(
    () =>
      regimes.map((regime) => ({
        value: regime.id,
        label: regime.name ?? `Regime ${regime.id}`,
      })),
    [regimes]
  );

  const rankOptions = useMemo<RankOption[]>(
    () =>
      academicRanks.map((rank) => ({
        value: rank.id,
        label: `Rank ${rank.rank}`,
      })),
    [academicRanks]
  );

  return {
    regimes,
    regimeOptions,
    rangeLabel:
      getRangesLabel(regimes[0]?.awardType ?? '') || DEFAULT_RANGE_LABEL,
    rankOptions,
    isRanksLoading: academicRanksQuery.isLoading,
    customLogicCategories,
    isCustomLogicLoading: customLogicQuery.isLoading,
    isPrefillReady: !regimesQuery.isLoading && !customLogicQuery.isLoading,
    defaultAwardLogic: awardLogicDictionaryQuery.data?.[0]
      ?.name as CalculationRuleRequest['awardLogic'],
    eligibilityCriteriaOptions,
    eligibilityOperandOptions,
  };
};
